import { useMemo } from 'react';
import { CategoryIcon } from '../components/CategoryIcon';
import { MiniStat } from '../components/MiniStat';
import { Panel } from '../components/Panel';
import { StreakDot } from '../components/StreakDot';
import { addDays } from '../lib/date';
import { useStore } from '../lib/store';
import { currentStreak, longestStreak } from '../lib/streak';
import { FALLBACK_CAT } from '../lib/types';

export function StatsScreen() {
  const { goals, categories, categoryMap, checkInIndex } = useStore();
  const habits = goals.filter(g => g.type === 'habit' && !g.archived_at);
  const now = new Date();

  const topStreak = habits.reduce(
    (m, g) => Math.max(m, currentStreak(checkInIndex, g.id, now)),
    0,
  );
  const longest = habits.reduce(
    (m, g) => Math.max(m, longestStreak(checkInIndex, g.id)),
    0,
  );
  const totalCheckIns = [...checkInIndex.values()].reduce((n, s) => n + s.size, 0);

  const bars = useMemo(() => {
    // Last 12 weeks of check-ins across all habits.
    const out: number[] = [];
    for (let w = 11; w >= 0; w--) {
      const end = addDays(now, -w * 7);
      const start = addDays(end, -6);
      let n = 0;
      for (const set of checkInIndex.values()) {
        for (const d of set) {
          const t = new Date(d).getTime();
          if (t >= start.setHours(0, 0, 0, 0) && t <= end.setHours(23, 59, 59, 999)) n++;
        }
      }
      out.push(n);
    }
    return out;
  }, [checkInIndex, now]);

  const barMax = Math.max(1, ...bars);

  const byCat = categories.map(c => {
    const meta = categoryMap.get(c.key) ?? FALLBACK_CAT;
    return {
      ...meta,
      count: goals.filter(g => g.cat === c.key && !g.archived_at).length,
    };
  });
  const catMax = Math.max(1, ...byCat.map(c => c.count));

  const topStreaks = [...habits]
    .map(g => ({ g, s: currentStreak(checkInIndex, g.id, now) }))
    .sort((a, b) => b.s - a.s)
    .slice(0, 4);

  return (
    <div className="grid-stats">
      <Panel title="HOMESTEAD LEDGER">
        <div className="stats-toprow">
          <MiniStat label="CURRENT STREAK" value={`${topStreak}d`} icon="dot" streak={topStreak} />
          <MiniStat label="LONGEST EVER" value={`${longest}d`} icon="star" />
          <MiniStat label="TOTAL TENDED" value={String(totalCheckIns)} />
        </div>
        <div className="section-label">LAST 12 WEEKS</div>
        <div className="bar-chart">
          {bars.map((b, i) => (
            <div
              key={i}
              className={`bar-chart__bar ${
                i === bars.length - 1 ? 'bar-chart__bar--latest' : ''
              }`}
              style={{ height: `${Math.max(2, (b / barMax) * 100)}%` }}
              title={`Wk ${i + 1}: ${b}`}
            />
          ))}
        </div>
        <div className="bar-chart-axis">
          <span>12w ago</span>
          <span>now</span>
        </div>
      </Panel>

      <div className="screens-col">
        <Panel title="BY CATEGORY">
          {byCat.map(c => {
            const pct = (c.count / catMax) * 100;
            return (
              <div key={c.key} className="cat-row">
                <div className="cat-row__header">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <CategoryIcon slug={c.icon} size={18} alt="" />
                    {c.label}
                  </span>
                  <span>{c.count}</span>
                </div>
                <div className="cat-row__track">
                  <div
                    className="cat-row__fill"
                    style={{ width: `${Math.min(100, pct)}%`, background: c.color }}
                  />
                </div>
              </div>
            );
          })}
        </Panel>

        <Panel title="TOP STREAKS">
          <div className="streaks-list">
            {topStreaks.length === 0 && (
              <div style={{ fontFamily: 'VT323', fontSize: 18, color: '#6b3a1d' }}>
                No habits sown yet.
              </div>
            )}
            {topStreaks.map(({ g, s }) => (
              <div key={g.id} className="streaks-row">
                <StreakDot streak={s} />
                <span style={{ flex: 1 }}>{g.title}</span>
                <span className="streaks-row__count">{s}d</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
